import { Button, Spinner } from "@scoach/ui";
import { useRef, useState } from "react";

import { Markdown } from "../../companies/Markdown.tsx";
import { streamSse } from "../../../lib/http.ts";

type Turn = { role: "user" | "model"; text: string };

/**
 * "Talk with the transcript" — ask free-text questions answered ONLY from this
 * meeting's transcript. Streams the answer token-by-token from the server
 * (`/meetings/:id/transcript-chat/stream`), which runs on Vertex so it works for
 * any viewer without a personal Gemini key. Keeps a short in-memory Q&A history so
 * follow-ups have context (not persisted).
 */
export function TranscriptChat({ meetingId }: { meetingId: string }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  async function ask() {
    const question = input.trim();
    if (!question || streaming) return;
    setError("");
    setInput("");
    // History sent to the server = the turns BEFORE this question.
    const history = turns.map((t) => ({ role: t.role, text: t.text }));
    setTurns((prev) => [...prev, { role: "user", text: question }, { role: "model", text: "" }]);
    setStreaming(true);
    try {
      await streamSse(
        `/meetings/${meetingId}/transcript-chat/stream`,
        { question, history },
        {
          onDelta: (delta) => {
            setTurns((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "model")
                next[next.length - 1] = { ...last, text: last.text + delta };
              return next;
            });
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
          },
        },
      );
    } catch (e) {
      setError((e as Error).message || "Couldn't answer that. Try again.");
      // Drop the empty model turn on failure so it doesn't linger blank.
      setTurns((prev) => {
        const last = prev[prev.length - 1];
        return last && last.role === "model" && !last.text ? prev.slice(0, -1) : prev;
      });
    } finally {
      setStreaming(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 16,
        border: "1px solid var(--border, #e0e0e0)",
        borderRadius: 10,
        background: "var(--surface, #fff)",
        display: "flex",
        flexDirection: "column",
        maxHeight: 460,
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--border-soft, #eef0f3)",
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        Ask about this meeting
        <span style={{ fontWeight: 400, color: "var(--text-3)", marginLeft: 8, fontSize: 12 }}>
          Answers come only from the transcript.
        </span>
      </div>

      <div
        ref={scrollRef}
        style={{ overflowY: "auto", padding: "12px 14px", flex: 1, minHeight: 0 }}
      >
        {turns.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>
            e.g. "What did the customer commit to?", "Which objections came up?", "Summarize the
            technical concerns."
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {turns.map((t, i) =>
              t.role === "user" ? (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only chat log
                  key={i}
                  style={{ alignSelf: "flex-end", maxWidth: "85%" }}
                >
                  <div
                    style={{
                      background: "var(--gc-blue-50, #e8f0fe)",
                      color: "var(--text-1)",
                      borderRadius: 10,
                      padding: "8px 12px",
                      fontSize: 13,
                    }}
                  >
                    {t.text}
                  </div>
                </div>
              ) : (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only chat log
                  key={i}
                  style={{ alignSelf: "flex-start", maxWidth: "92%" }}
                >
                  {t.text ? (
                    <Markdown>{t.text}</Markdown>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        color: "var(--text-3)",
                      }}
                    >
                      <Spinner size={14} /> Thinking…
                    </div>
                  )}
                </div>
              ),
            )}
          </div>
        )}
        {error && (
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--danger, #d93025)" }}>{error}</div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 12px",
          borderTop: "1px solid var(--border-soft, #eef0f3)",
        }}
      >
        <input
          type="text"
          className="setup-input"
          style={{ flex: 1 }}
          value={input}
          placeholder="Ask a question about this meeting…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void ask();
            }
          }}
          disabled={streaming}
        />
        <Button onClick={() => void ask()} disabled={streaming || !input.trim()}>
          {streaming ? "…" : "Ask"}
        </Button>
      </div>
    </div>
  );
}
